import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { openPaystackPopup } from "../services/paystackService";
import { investAPI } from "../services/api";

// ── Scroll reveal ─────────────────────────────────────────────────────────────
function useReveal() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("on")),
      { threshold: 0.08 }
    );
    document.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ── Package definitions ───────────────────────────────────────────────────────
const PACKAGES = [
  {
    id: "silver",
    name: "Silver",
    badge: "Entry Partnership",
    amount: 10000,
    monthly: 500,
    color: "#94a3b8",
    glow: "rgba(148,163,184,0.15)",
    border: "rgba(148,163,184,0.3)",
    emoji: "🥈",
    perks: [
      "Shared bike ownership",
      "GHS 500 monthly returns",
      "6-month minimum term",
      "Capital fully protected",
      "Roll over or withdraw at term",
    ],
  },
  {
    id: "gold",
    name: "Gold",
    badge: "Shared Bike Partnership",
    amount: 20000,
    monthly: 1000,
    color: "#f5a623",
    glow: "rgba(245,166,35,0.18)",
    border: "rgba(245,166,35,0.45)",
    emoji: "🥇",
    featured: true,
    perks: [
      "Shared premium bike ownership",
      "GHS 1,000 monthly returns",
      "6-month minimum term",
      "Capital fully protected",
      "Returns increase from month 4",
    ],
  },
  {
    id: "premium",
    name: "Premium",
    badge: "Full Bike Ownership",
    amount: 30000,
    monthly: 1500,
    color: "#a78bfa",
    glow: "rgba(167,139,250,0.15)",
    border: "rgba(167,139,250,0.35)",
    emoji: "💎",
    perks: [
      "100% sole bike ownership",
      "GHS 1,500 monthly returns",
      "6-month minimum term",
      "Capital fully protected",
      "Priority returns from month 4",
    ],
  },
];

// ── Stat ticker ───────────────────────────────────────────────────────────────
function StatTicker({ value, label, prefix = "", suffix = "" }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = value / 60;
    const timer = setInterval(() => {
      start += step;
      if (start >= value) { setCount(value); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [value]);
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontFamily: "var(--font-head)", fontSize: "clamp(28px,4vw,48px)", fontWeight: 800, letterSpacing: "-2px", color: "var(--accent)" }}>
        {prefix}{count.toLocaleString()}{suffix}
      </div>
      <div style={{ fontSize: 13, color: "var(--text3)", marginTop: 4 }}>{label}</div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Invest() {
  useReveal();
  const navigate = useNavigate();

  const [selected, setSelected] = useState("gold");
  const [loading, setLoading] = useState(false);

  // Form fields
  const [name, setName]     = useState("");
  const [email, setEmail]   = useState("");
  const [phone, setPhone]   = useState("");

  const pkg = PACKAGES.find((p) => p.id === selected);

  const handleSubmit = async () => {
    if (!name.trim())           { toast.error("Enter your full name");    return; }
    if (!email.includes("@"))   { toast.error("Enter a valid email");     return; }
    if (phone.replace(/\D/g, "").length < 9) { toast.error("Enter a valid phone number"); return; }

    setLoading(true);

    try {
      // 1. Register investment intent on backend
      const ref = `KWKINVEST-${Date.now()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;

      await investAPI.register({
        name,
        email,
        phone,
        package: selected,
        amount: pkg.amount,
        reference: ref,
      });

      // 2. Open Paystack inline popup
      openPaystackPopup({
        email,
        amount: pkg.amount,
        reference: ref,
        payment_method: "card",
        onSuccess: async (paystackRef) => {
          // Confirm directly — don't rely solely on the webhook.
          // This fires immediately after Paystack closes, so the DB
          // is always updated even on localhost with no public webhook URL.
          try {
            await investAPI.confirm({ reference: ref, paystack_ref: paystackRef });
          } catch (_) {
            // Webhook may have already confirmed it — safe to ignore
          }
          navigate("/invest/success", {
            state: { name, pkg: pkg.name, amount: pkg.amount, monthly: pkg.monthly, ref },
          });
        },
        onClose: () => {
          toast("Payment cancelled. Your details are saved — try again anytime.", { icon: "ℹ️" });
          setLoading(false);
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", paddingTop: 80 }}>

      {/* ── Ambient glow ── */}
      <div style={{ position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)", width: 900, height: 600, background: "radial-gradient(ellipse,rgba(245,166,35,0.06) 0%,transparent 70%)", pointerEvents: "none", zIndex: 0 }} />

      {/* ── Hero ── */}
      <section style={{ padding: "64px 6% 48px", textAlign: "center", position: "relative" }}>
        <div className="reveal" style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,0.1)", border: "1px solid rgba(245,166,35,0.25)", borderRadius: 100, padding: "6px 16px", fontSize: 12, fontWeight: 700, color: "var(--accent)", marginBottom: 24, letterSpacing: "0.5px", textTransform: "uppercase" }}>
            🚀 Investment Opportunity
          </div>
          <h1 style={{ fontSize: "clamp(32px,5vw,68px)", fontWeight: 800, letterSpacing: "-2.5px", lineHeight: 1.08, marginBottom: 20 }}>
            Own a delivery bike.<br />
            <span style={{ background: "linear-gradient(135deg,var(--accent),var(--accent2))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Earn every month.
            </span>
          </h1>
          <p style={{ fontSize: "clamp(15px,1.8vw,18px)", color: "var(--text2)", lineHeight: 1.7, maxWidth: 560, margin: "0 auto 40px" }}>
            Partner with Kwikie Express and invest in our expanding delivery fleet.
            We manage the riders and operations — you collect steady monthly returns.
          </p>

          {/* Stats row */}
          <div style={{ display: "flex", justifyContent: "center", gap: "clamp(24px,5vw,72px)", flexWrap: "wrap", padding: "32px 0", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
            <StatTicker value={6} label="Month minimum term" suffix=" mo" />
            <StatTicker value={18} label="Annual ROI (Gold)" suffix="%" />
            <StatTicker value={100} label="Capital protected" suffix="%" />
          </div>
        </div>
      </section>

      {/* ── Package selector ── */}
      <section style={{ padding: "48px 6%", position: "relative" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="reveal" style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text3)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>Choose your package</div>
            <h2 style={{ fontSize: "clamp(22px,3vw,36px)", fontWeight: 800, letterSpacing: "-1px" }}>Three ways to invest</h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 20 }}>
            {PACKAGES.map((p) => {
              const isSelected = selected === p.id;
              return (
                <div
                  key={p.id}
                  onClick={() => setSelected(p.id)}
                  className="reveal"
                  style={{
                    background: isSelected ? p.glow : "var(--bg2)",
                    border: `2px solid ${isSelected ? p.border : "var(--border)"}`,
                    borderRadius: 20,
                    padding: "28px 24px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    position: "relative",
                    boxShadow: isSelected ? `0 0 40px ${p.glow}` : "none",
                    transform: isSelected ? "translateY(-4px)" : "none",
                  }}
                  onMouseOver={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = p.border; e.currentTarget.style.transform = "translateY(-2px)"; } }}
                  onMouseOut={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.transform = "none"; } }}
                >
                  {p.featured && (
                    <div style={{ position: "absolute", top: -13, left: "50%", transform: "translateX(-50%)", background: "var(--accent)", color: "#000", fontFamily: "var(--font-head)", fontSize: 10, fontWeight: 800, padding: "4px 14px", borderRadius: 100, whiteSpace: "nowrap", letterSpacing: "0.5px" }}>
                      MOST POPULAR
                    </div>
                  )}

                  {/* Radio indicator */}
                  <div style={{ position: "absolute", top: 20, right: 20, width: 20, height: 20, borderRadius: "50%", border: `2px solid ${isSelected ? p.color : "var(--border2)"}`, background: isSelected ? p.color : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}>
                    {isSelected && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#000" }} />}
                  </div>

                  <div style={{ fontSize: 28, marginBottom: 12 }}>{p.emoji}</div>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, marginBottom: 4, color: p.color }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 20 }}>{p.badge}</div>

                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>INVEST</div>
                    <div style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, letterSpacing: "-1px" }}>
                      GHS {p.amount.toLocaleString()}
                    </div>
                  </div>

                  <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "12px 16px", marginBottom: 20, border: "1px solid var(--border)" }}>
                    <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>EARN MONTHLY</div>
                    <div style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800, color: p.color }}>
                      GHS {p.monthly.toLocaleString()}
                    </div>
                  </div>

                  <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
                    {p.perks.map((perk) => (
                      <li key={perk} style={{ fontSize: 13, color: "var(--text2)", display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <span style={{ color: p.color, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
                        {perk}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Registration form ── */}
      <section style={{ padding: "16px 6% 80px", position: "relative" }}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <div className="reveal" style={{ background: "var(--bg2)", border: `1px solid ${pkg.border}`, borderRadius: 24, padding: "clamp(24px,4vw,44px)", boxShadow: `0 0 60px ${pkg.glow}` }}>

            {/* Header */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <span style={{ fontSize: 24 }}>{pkg.emoji}</span>
                <div>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 800 }}>
                    {pkg.name} Package
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text3)" }}>
                    GHS {pkg.amount.toLocaleString()} investment · GHS {pkg.monthly.toLocaleString()}/mo returns
                  </div>
                </div>
                <button
                  onClick={() => document.querySelector(".reveal")?.scrollIntoView({ behavior: "smooth" })}
                  style={{ marginLeft: "auto", background: "none", border: "1px solid var(--border)", borderRadius: 8, padding: "5px 12px", fontSize: 12, color: "var(--text3)", cursor: "pointer", fontFamily: "var(--font-body)" }}
                >
                  Change
                </button>
              </div>
              <div style={{ height: 2, borderRadius: 2, background: `linear-gradient(90deg,${pkg.color},transparent)` }} />
            </div>

            {/* Form fields */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Full Name *</label>
                <input
                  className="form-input"
                  placeholder="Kwame Mensah"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Email Address *</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="kwame@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Phone Number *</label>
                <input
                  className="form-input"
                  placeholder="+233 XX XXX XXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                />
              </div>
            </div>

            {/* Summary box */}
            <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: 12, padding: "16px", margin: "24px 0 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: "var(--text3)" }}>Selected package</span>
                <span style={{ fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14, color: pkg.color }}>{pkg.name} — {pkg.badge}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: "var(--text3)" }}>Investment amount</span>
                <span style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 16 }}>GHS {pkg.amount.toLocaleString()}</span>
              </div>
              <div style={{ height: 1, background: "var(--border)", margin: "12px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "var(--text3)" }}>Monthly return</span>
                <span style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 16, color: pkg.color }}>GHS {pkg.monthly.toLocaleString()}/mo</span>
              </div>
            </div>

            <button
              className="btn-primary"
              style={{
                width: "100%",
                justifyContent: "center",
                padding: "16px",
                fontSize: 16,
                fontWeight: 800,
                background: pkg.id === "gold" ? "var(--accent)" : pkg.id === "premium" ? "linear-gradient(135deg,#a78bfa,#7c3aed)" : "linear-gradient(135deg,#94a3b8,#64748b)",
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <span className="spinner" /> : `Invest GHS ${pkg.amount.toLocaleString()} →`}
            </button>

            <p style={{ fontSize: 12, color: "var(--text3)", textAlign: "center", marginTop: 14, lineHeight: 1.6 }}>
              🔒 Secured by Paystack · Capital protected · 6-month minimum term
            </p>
          </div>

          {/* Trust signals */}
          <div className="reveal" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 20 }}>
            {[
              { icon: "🛡️", label: "Capital Protected" },
              { icon: "📈", label: "Growing Returns" },
              { icon: "🔄", label: "Roll Over or Exit" },
            ].map((t) => (
              <div key={t.label} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "14px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 20, marginBottom: 6 }}>{t.icon}</div>
                <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 600 }}>{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section style={{ padding: "64px 6%", background: "var(--bg2)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div className="reveal" style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text3)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>Simple process</div>
            <h2 style={{ fontSize: "clamp(22px,3vw,36px)", fontWeight: 800, letterSpacing: "-1px" }}>How your investment works</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 24 }}>
            {[
              { step: "01", icon: "📋", title: "Register & Pay", desc: "Choose a package, fill the form, and complete payment securely via Paystack." },
              { step: "02", icon: "🏍️", title: "Bike Deployed", desc: "Your investment funds a delivery motorbike that joins our active fleet immediately." },
              { step: "03", icon: "💸", title: "Monthly Returns", desc: "Collect GHS returns from your bike's delivery activity every month, starting month 1." },
              { step: "04", icon: "🔄", title: "Exit or Roll Over", desc: "After 6 months, withdraw your full capital or roll over to keep earning." },
            ].map((s) => (
              <div key={s.step} className="reveal" style={{ padding: "24px 20px", background: "var(--bg3)", borderRadius: 16, border: "1px solid var(--border)", position: "relative", overflow: "hidden" }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 48, fontWeight: 800, color: "var(--border2)", position: "absolute", top: -8, right: 12, lineHeight: 1, userSelect: "none" }}>{s.step}</div>
                <div style={{ fontSize: 28, marginBottom: 12 }}>{s.icon}</div>
                <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{s.title}</div>
                <div style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: "64px 6% 80px" }}>
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <div className="reveal" style={{ textAlign: "center", marginBottom: 40 }}>
            <h2 style={{ fontSize: "clamp(22px,3vw,36px)", fontWeight: 800, letterSpacing: "-1px" }}>Common questions</h2>
          </div>
          {[
            { q: "Is my capital guaranteed?", a: "Yes. Your initial investment is fully protected. You can withdraw your full capital at the end of the 6-month period." },
            { q: "When do monthly returns start?", a: "Returns begin from month 1 and increase substantially from month 4 as operations scale and delivery volumes grow." },
            { q: "How do I receive my monthly returns?", a: "Returns are paid directly to your mobile money or bank account on a fixed monthly schedule." },
            { q: "What happens after 6 months?", a: "You can choose to withdraw your full capital, or roll over the investment to continue earning monthly returns." },
            { q: "Can I invest in multiple packages?", a: "Yes. You can invest in multiple packages or upgrade your package at any time after the initial term." },
          ].map((faq, i) => (
            <FAQItem key={i} q={faq.q} a={faq.a} />
          ))}
        </div>
      </section>
    </div>
  );
}

// ── FAQ accordion ─────────────────────────────────────────────────────────────
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="reveal" style={{ borderBottom: "1px solid var(--border)", padding: "18px 0" }}>
      <button
        onClick={() => setOpen((p) => !p)}
        style={{ width: "100%", textAlign: "left", background: "none", border: "none", color: "var(--text)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700 }}
      >
        {q}
        <span style={{ fontSize: 18, color: "var(--accent)", flexShrink: 0, transform: open ? "rotate(45deg)" : "none", transition: "transform 0.2s" }}>+</span>
      </button>
      {open && (
        <div style={{ fontSize: 14, color: "var(--text2)", lineHeight: 1.7, marginTop: 12, paddingRight: 32, animation: "fadeUp 0.2s ease both" }}>
          {a}
        </div>
      )}
    </div>
  );
}